/**
 * You are warned before you are attacked.
 *
 * `director.ts` states the rule this file exists to keep: *everything the
 * director decides is visible before it happens; difficulty comes from the
 * player's choices, never from surprise.* The settlement layer honours it — the
 * scouting panel names the next wave before it is bought. The world map did not.
 * A hostile host set out from a province you could not see, `moveT` counted down
 * in silence, and the first you knew of it was a battle prompt.
 *
 * The repair is scouting range rather than a herald, and the reason is that
 * range is a *rule* while a herald is a favour. A herald that tells you what is
 * coming tells you regardless of where you are looking, which makes the map's
 * geography stop mattering; range means the answer to "why did I not see that"
 * is always "look at what you hold", which is a question the board can answer.
 *
 * The guarantee, stated exactly:
 *
 *   A nation sees every province it holds, every province one hop from those,
 *   and every province one hop from a host of its own. Every march *into* ground
 *   it holds therefore sets out from a province it can see, and the shortest
 *   march on the board is one full month. So no hostile army can arrive on
 *   ground you hold without having stood in your warning list for at least one
 *   month first — by arithmetic, not by a die roll.
 *
 * `tests/campaign.test.ts` asserts the arithmetic rather than trusting it.
 *
 * ── What that guarantee is worth in seconds ──────────────────────────────
 *
 * It used to be worth one click, which is to say: as long as the player wanted.
 * The realm advances on its own now, so the same sentence has to be cashed out
 * in time, and it is: a month is `MONTH_SECONDS` — 280 seconds of simulated
 * time, a little under five minutes at normal speed. That is the floor. A march
 * across the board is several of them.
 *
 * The honest caveat, recorded here because it is the one thing this change
 * makes worse: **the floor scales with the speed control.** At ×8 the same
 * warning is thirty-five seconds of real time, and the player is not eight times
 * faster. `main.ts` answers that by dropping the game back to normal pace the
 * moment a host takes the road to the player's own capital — the one warning
 * that cannot be allowed to go by unread — but every other warning is only as
 * good as the speed the player left the game running at. It is a real cost of
 * leaving turns behind and it is not fully bought off.
 */

import { neighbours } from './graph.ts';
import { armiesStandingIn, armyPoints } from './armies.ts';
import { threateningVoyages } from './fleets.ts';
import { isMarching, roadName } from './marches.ts';
import { sameRealm } from './diplomacy.ts';
import { atWar, logLine, nationName, provinceName } from './state.ts';
import {
  SCOUT_RANGE,
  WarnKind,
  BANDIT_NATION,
  type CampaignState,
  type MarchWarning,
} from './types.ts';

/**
 * Which provinces a nation can see into, as a flat boolean array.
 *
 * Held ground, ground a host of its own is standing on, and everything within
 * `SCOUT_RANGE` hops of either. Marching hosts see nothing: an army strung out
 * on a road is not scouting, and letting it would mean a host could be used as a
 * moving eye, which quietly makes the warning list a function of micromanagement.
 */
export function visibleProvinces(c: CampaignState, nation: number): boolean[] {
  const seen = new Array<boolean>(c.provinces.length).fill(false);
  const frontier: number[] = [];
  for (let p = 0; p < c.provinces.length; p++) {
    if (c.provinces[p].owner === nation) {
      seen[p] = true;
      frontier.push(p);
    }
  }
  for (const a of c.armies) {
    if (a.owner !== nation || isMarching(a)) continue;
    if (!seen[a.at]) {
      seen[a.at] = true;
      frontier.push(a.at);
    }
  }
  let edge = frontier;
  for (let hop = 0; hop < SCOUT_RANGE; hop++) {
    const next: number[] = [];
    for (const p of edge) {
      for (const q of neighbours(c, p)) {
        if (seen[q]) continue;
        seen[q] = true;
        next.push(q);
      }
    }
    edge = next;
  }
  return seen;
}

/**
 * Rebuild the player's warning list, and say out loud anything new.
 *
 * Called once per turn, at the end of the march phase — after armies have moved,
 * so `turns` is the number of turns the player has left to do something about it,
 * and before the AI's council sits, so an order given this turn is warned about
 * next turn rather than the same one.
 *
 * The list is rebuilt from scratch every turn rather than maintained, but
 * `sinceTurn` is carried across from the matching warning of the previous turn.
 * That is the field that makes the fairness rule checkable: a warning whose
 * `sinceTurn` is this turn is news, and one whose `sinceTurn` is older is
 * something the player has already had a chance to answer.
 */
export function refreshWarnings(c: CampaignState): void {
  const nation = c.playerNation;
  const previous = c.warnings;
  const seen = visibleProvinces(c, nation);
  const out: MarchWarning[] = [];

  // 1. Hosts on the roads. A march is visible when either end of its road is —
  //    you see the host set out, or you see the ground it is coming to.
  for (const a of c.armies) {
    if (!isMarching(a)) continue;
    if (a.owner === nation) continue;
    if (!atWar(c, nation, a.owner)) continue;
    if (!seen[a.moveFrom] && !seen[a.moveTo]) continue;
    // Only trouble that is actually coming for you. A war two provinces over is
    // the chronicle's business, not the warning list's.
    if (!threatens(c, nation, a.moveTo)) continue;
    out.push({
      kind: WarnKind.March,
      nation: a.owner,
      armyId: a.id,
      from: a.moveFrom,
      to: a.moveTo,
      turns: a.moveT,
      strength: Math.round(armyPoints(a)),
      sinceTurn: c.turn,
    });
  }

  /**
   * 2. Hosts standing next to ground you hold. Not yet a march, and precisely
   *    the moment at which a player can still do something cheap about it.
   *
   * This used to require being at war with them, which was harmless on a board
   * where everyone always was. Peace as the opening position turned that gate
   * into a hole: a crown could quietly stage a host on your border with no
   * warning at all, declare, and march — and you would learn of it from the
   * March warning alone. The stated guarantee survived, because arrival is
   * still a month away when that warning is raised, but the *effective* notice
   * went from two signals to one, and it did so silently.
   *
   * A host is a host. If your scouts can see the ground it stands on, they can
   * see it standing there, and whether a herald has been sent yet is not
   * something a picket knows. Being at war is not what makes an army visible —
   * it is what makes it *your problem*, which is why the warning still says so.
   */
  for (let p = 0; p < c.provinces.length; p++) {
    if (c.provinces[p].owner !== nation) continue;
    for (const q of neighbours(c, p)) {
      if (!seen[q]) continue;
      for (const a of armiesStandingIn(c, q)) {
        if (a.owner === nation || sameRealm(c, nation, a.owner)) continue;
        // Already reported as on the road: do not report it twice. Scoped to
        // `March` because `armyId` does not mean the same thing in every kind —
        // a `Landing` carries a *fleet* id, and an unscoped test would let
        // fleet 3 silence the warning about army 3.
        if (out.some((w) => w.kind === WarnKind.March && w.armyId === a.id)) continue;
        out.push({
          kind: WarnKind.Massing,
          nation: a.owner,
          armyId: a.id,
          from: q,
          to: p,
          turns: 0,
          strength: Math.round(armyPoints(a)),
          sinceTurn: c.turn,
        });
      }
    }
  }

  /**
   * 3. Convoys bound for a coast you hold.
   *
   * The fairness rule — nothing arrives that was not on the player's map first
   * — was written when the only way to reach somebody was to walk, and a
   * landing would have broken it outright: a lane is three to seven times
   * quicker than the road it replaces, so a host could make a coast the player
   * had never watched it approach.
   *
   * The repair is the same repair `March` is, and it is worth being precise
   * about why it holds. The *visibility* rule is deliberately weaker than the
   * one for roads: a voyage is warned about when either end of its lane is
   * seen, and the far end is ground the player holds by construction, so every
   * convoy bound for the player is visible for its whole voyage regardless of
   * whether they can see the harbour it sailed from. That is a real difference
   * from a march — you cannot tell where a fleet came from — and it is the
   * right one. What the player is owed is the time, and they get all of it:
   * the shortest lane on the board is a full month.
   *
   * Empty fleets raise nothing. A warship on its own takes no ground, and a
   * warning list that cried out for every hull on the water would bury the one
   * that means an army is coming.
   */
  for (const f of threateningVoyages(c, nation)) {
    let strength = 0;
    for (const id of f.cargo) {
      const a = c.armies.find((x) => x.id === id);
      if (a) strength += armyPoints(a);
    }
    out.push({
      kind: WarnKind.Landing,
      nation: f.owner,
      // The fleet's id, not an army's. `kind` says which list it indexes, and
      // the two are only ever read together — see `warningsFor`.
      armyId: f.id,
      from: f.moveFrom,
      to: f.moveTo,
      turns: f.moveT,
      strength: Math.round(strength),
      sinceTurn: c.turn,
    });
  }

  // 4. Brigands on your own ground. They are not marching anywhere, but they are
  //    taking a cut every turn, and a cost the player cannot see is a cost the
  //    player cannot decide about.
  for (const camp of c.camps) {
    if (c.provinces[camp.at].owner !== nation) continue;
    out.push({
      kind: WarnKind.Bandits,
      nation: BANDIT_NATION,
      armyId: -1,
      from: camp.at,
      to: camp.at,
      turns: 0,
      strength: Math.round(camp.strength),
      sinceTurn: c.turn,
    });
  }

  out.sort((a, b) => a.kind - b.kind || a.turns - b.turns || a.armyId - b.armyId || a.to - b.to);

  // Carry `sinceTurn` across, and announce only what is genuinely new.
  for (const w of out) {
    const old = previous.find(
      (o) => o.kind === w.kind && o.armyId === w.armyId && o.to === w.to,
    );
    if (old) {
      w.sinceTurn = old.sinceTurn;
      continue;
    }
    announce(c, w);
  }
  c.warnings = out;
}

/**
 * Does a march on this province concern the player?
 *
 * Ground the player holds, obviously — but also ground one hop from it, because
 * a host taking the province next door is the last quiet turn before it takes
 * yours, and the fairness rule is about having time to decide rather than about
 * having time to react.
 */
function threatens(c: CampaignState, nation: number, province: number): boolean {
  if (c.provinces[province].owner === nation) return true;
  return neighbours(c, province).some((q) => c.provinces[q].owner === nation);
}

function announce(c: CampaignState, w: MarchWarning): void {
  if (w.kind === WarnKind.March) {
    // Months, because a month is what a march is actually measured in now that
    // the realm keeps its own time. `turns` is the same number it always was.
    const when = w.turns <= 1 ? 'within the month' : `in ${w.turns} months`;
    logLine(
      c,
      `Riders: a host of ${nationName(c, w.nation)} is on ${roadName(c, w.from, w.to)}, ` +
        `${w.strength} strong. It reaches ${provinceName(w.to)} ${when}.`,
    );
    return;
  }
  if (w.kind === WarnKind.Massing) {
    logLine(
      c,
      `Riders: ${nationName(c, w.nation)} has ${w.strength} standing at ${provinceName(w.from)}, ` +
        `one march from ${provinceName(w.to)}.`,
    );
    return;
  }
  if (w.kind === WarnKind.Landing) {
    const when = w.turns <= 1 ? 'within the month' : `in ${w.turns} months`;
    logLine(
      c,
      `Fishermen: ${nationName(c, w.nation)} has ${w.strength} on the water, standing in for ` +
        `${provinceName(w.to)}. They make landfall ${when}.`,
    );
    return;
  }
  logLine(c, `Brigands have settled on ${provinceName(w.from)} and are taking their share.`);
}

/** Warnings about a particular province, for the world map's province panel. */
export function warningsFor(c: CampaignState, province: number): MarchWarning[] {
  return c.warnings.filter((w) => w.to === province || w.from === province);
}

/** The soonest arrival in the list, or -1 when nothing is coming. */
export function soonestArrival(c: CampaignState): number {
  let soonest = -1;
  for (const w of c.warnings) {
    if (w.kind !== WarnKind.March) continue;
    if (soonest < 0 || w.turns < soonest) soonest = w.turns;
  }
  return soonest;
}
